/**
 * Carousel class provides functionality for a draggable, scrollable carousel 
 * component with support for smooth scrolling and resizing.
 */
export class Carousel {
    private dotnetRef: any; // Reference to the .NET object for invoking .NET methods
    private elementRef: HTMLElement; // Reference to the root element of the carousel
    private itemsContainerRef: HTMLElement; // Reference to the container holding carousel items
    private isDragging: boolean = false; // Indicates if the carousel is in dragging mode
    private startX: number = 0; // Starting X coordinate for dragging
    private scrollLeft: number = 0; // Scroll position before dragging starts
    private resizeObserver: ResizeObserver; // Observer to handle carousel resizing

    /**
     * Initializes a new instance of the Carousel class.
     * Sets up event listeners for drag and scroll handling.
     *
     * @param dotNetRef - Reference to the .NET object
     * @param elementRef - Root HTML element of the carousel
     */
    constructor(dotNetRef: any, elementRef: HTMLElement) {
        this.dotnetRef = dotNetRef;
        this.elementRef = elementRef;
        this.itemsContainerRef = this.elementRef.querySelector('.items') as HTMLElement;

        this.Init();
        this.itemsContainerRef.addEventListener('mousedown', this.OnMouseDown.bind(this));
        this.itemsContainerRef.addEventListener('touchstart', this.OnMouseDown.bind(this));

        this.itemsContainerRef.addEventListener('scroll', this.OnScroll.bind(this));
        this.resizeObserver = new ResizeObserver(() => {
            this.OnResize();
        });
        this.resizeObserver.observe(this.itemsContainerRef);
    }

    /**
     * Scrolls to a specified item in the carousel.
     *
     * @param index - Index of the item to scroll to
     * @param behavior - Scrolling behavior ('smooth' or 'instant')
     */
    public MoveTo(index: number, behavior: ScrollBehavior = 'smooth') {
        const itemToScrollTo = this.itemsContainerRef.querySelectorAll(`.item-wrapper:not(.cloned)`).item(index) as HTMLElement;
        if (itemToScrollTo === undefined) {
            return;
        }

        this.itemsContainerRef.scrollTo({
            left: itemToScrollTo.offsetLeft,
            behavior: behavior
        });
    }

    /**
     * Initializes the carousel by setting up dimensions and positioning.
     */
    private Init() {
        this.OnResize().then(() => {
            this.MoveTo(this.GetItemIndex(this.GetFirstVisibleItem()), 'instant');
        });
    }

    /**
     * Handles the mousedown or touchstart event for initiating dragging.
     *
     * @param e - Mouse or touch event
     */
    private OnMouseDown(e: MouseEvent) {
        window.addEventListener('mouseup', this.OnMouseUp.bind(this));
        window.addEventListener('mousemove', this.OnMouseMove.bind(this));
        window.addEventListener('touchend', this.OnMouseUp.bind(this));
        window.addEventListener('touchmove', this.ResetToNonClonedItem.bind(this));

        this.isDragging = true;
        this.startX = e.pageX - this.itemsContainerRef.offsetLeft;
        this.scrollLeft = this.itemsContainerRef.scrollLeft;
        this.itemsContainerRef.classList.add('dragging');
    }

    /**
     * Handles the mouseup or touchend event for stopping dragging.
     */
    private OnMouseUp() {
        window.removeEventListener('mouseup', this.OnMouseUp.bind(this));
        window.removeEventListener('mousemove', this.OnMouseMove.bind(this));
        window.removeEventListener('touchend', this.OnMouseUp.bind(this));
        window.removeEventListener('touchmove', this.ResetToNonClonedItem.bind(this));

        this.isDragging = false;
        this.itemsContainerRef.classList.remove('dragging');
        const firstVisibleItem = this.GetFirstVisibleItem();

        this.MoveTo(this.GetItemIndex(firstVisibleItem));
    }

    /**
     * Handles the mousemove event for updating carousel position during dragging.
     *
     * @param e - Mouse event
     */
    private OnMouseMove(e: MouseEvent) {
        if (this.isDragging == false) {
            return;
        }

        e.preventDefault();
        const x = e.pageX - this.itemsContainerRef.offsetLeft;
        const walk = (x - this.startX) * 1.5; // Multiplier for scroll speed
        this.itemsContainerRef.scrollLeft = this.scrollLeft - walk;

        this.ResetToNonClonedItem();
    }

    /**
     * Adjusts the scroll position to show the non-cloned version of the visible item.
     */
    private ResetToNonClonedItem() {
        const firstItem = this.GetFirstVisibleItem();
        if (firstItem.classList.contains("cloned") == false) {
            return;
        }

        const nonClonedItem = this.itemsContainerRef.querySelectorAll(`.item-wrapper:not(.cloned)`).item(this.GetItemIndex(firstItem)) as HTMLElement;
        this.itemsContainerRef.scrollLeft += nonClonedItem.offsetLeft - firstItem.offsetLeft;
    }

    /**
     * Handles resizing of the carousel by recalculating visibility of items.
     */
    private async OnResize(): Promise<void> {
        const totalWidth = Array.from(this.itemsContainerRef.querySelectorAll('.item-wrapper:not(.cloned)'))
            .reduce((sum, item) => sum + (item as HTMLElement).getBoundingClientRect().width, 0);

        await this.dotnetRef.invokeMethodAsync('SetVisibility', totalWidth, this.itemsContainerRef.clientWidth);
    }

    /**
     * Handles scrolling by setting the index based on the visible item.
     */
    private async OnScroll(): Promise<void> {
        const firstVisibleIndex = this.GetFirstVisibleItem();
        await this.dotnetRef.invokeMethodAsync('SetIndexFromScroll', this.GetItemIndex(firstVisibleIndex));
    }

    /**
     * Gets the first visible item in the carousel based on scroll position.
     *
     * @returns The first visible HTML item element
     */
    private GetFirstVisibleItem(): HTMLElement {
        const containerRect = this.itemsContainerRef.getBoundingClientRect();

        for (let i = 0; i < this.itemsContainerRef.children.length; i++) {
            const item = this.itemsContainerRef.children[i] as HTMLElement;
            const itemRect = item.getBoundingClientRect();

            const itemVisibleWidth = Math.min(itemRect.right, containerRect.right) - Math.max(itemRect.left, containerRect.left);
            const itemWidth = itemRect.width;

            const visiblePercentage = itemVisibleWidth / itemWidth;

            if (visiblePercentage >= 0.5) {
                return item;
            }
        }

        return null;
    }

    /**
     * Gets the index of a specified item in the carousel.
     *
     * @param item - HTML element of the item
     * @returns The index of the item within non-cloned items
     */
    private GetItemIndex(item: HTMLElement): number {
        const items = Array.from(this.itemsContainerRef.children);
        const numberOfItems = this.itemsContainerRef.querySelectorAll('.item-wrapper:not(.cloned)').length;

        return items.indexOf(item) % numberOfItems;
    }

    /**
     * Disposes the carousel by removing event listeners and stopping observers.
     */
    public Dispose() {
        this.resizeObserver.unobserve(this.itemsContainerRef);
        this.itemsContainerRef.removeEventListener('mousedown', this.OnMouseDown.bind(this));
        this.itemsContainerRef.removeEventListener('touchstart', this.OnMouseDown.bind(this));
    }
}

/**
 * Initializes a new Carousel instance.
 *
 * @param dotNetRef - Reference to the .NET object
 * @param elementRef - Root HTML element of the carousel
 * @returns The Carousel instance
 */
window['InitCarousel'] = function (dotNetRef: any, elementRef: HTMLElement) {
    return new Carousel(dotNetRef, elementRef);
};